/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import {addSeconds, isToday, subDays} from 'date-fns';
import {User} from 'oidc-client';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {
    HSAdministeredAdHocDrug, HSAdministeredDrug,
    HSDrug,
    HSPackedMedication, HSUser,
    ReasonCode, SyringeDriverRunDto
} from 'server-openapi';
import styled from 'styled-components';
import {useCurrentUser} from '../../../../core/authn/UserProvider';
import {useMap} from '../../../../core/storage/hooks/UseStore';
import {DateUtils} from '../../../../core/utils/dateUtils';
import {Button} from '../../../../kit/Button';
import {Dialog} from '../../../../kit/Dialog';
import {DateInput} from '../../../../kit/Forms/DateInput';
import {Form} from '../../../../kit/Forms/Form';
import {FormGroup} from '../../../../kit/Forms/FormGroup';
import {RadioGroup} from '../../../../kit/Forms/RadioGroup';
import {Select} from '../../../../kit/Forms/Select';
import {TextArea} from '../../../../kit/Forms/TextArea';
import {TextInput} from '../../../../kit/Forms/TextInput';
import {Grid} from '../../../../kit/Grid';
import {Layout} from '../../../../kit/Layout';
import {Text} from '../../../../kit/Text';
import {toasts} from '../../../../kit/Toasts/Toaster';
import {useSyncCenter} from '../../../../syncstream/SyncCenterProvider';
import {useApiUtils} from '../../../../syncstream/utils/hooks/useApiUtils';
import {ResidentDetailsUtils} from '../../../../syncstream/utils/ResidentDetailsUtils';
import {useRoundSchedule} from '../../../Rounds/services/RoundScheduleProvider';
import {
  MedicationInformationProps
} from '../medicationInformation/MedicationInformationBox';
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";
import {Checkbox} from "../../../../kit/Checkbox";
import {colors} from "../../../../kit/Theme/Theme";
import MedicationInformationLabel from "../MedicationInformationLabel";
import {SecondCheckData} from "../MedicationListsTabbedRouter/MedicationListsTabbedRouter";
import {SyringeDriverRunActivityTypeDto} from "server-openapi/dist/generated-sdk/api";
import {SyncSyringeDriverRuns} from "../../../../syncstream/SyncSyringeDriverRun";
import {SecondCheckDialog} from "../SecondCheckDialog";
import {
    getRoundHierarchiesForPackedMedication,
} from "../../../../view_model/RoundHierarchy";
import {
    SyringeDriverConfirmMultipleDrugsDialog
} from "../syringeDrivers/SyringeDriverConfirmMultipleDrugsDialog";


interface SyringeDriverRunAdministrationFormProps extends MedicationInformationProps {
  drug: HSDrug;
  confirmationInitials?: string;
  confirmationUserId?: number;
  open: boolean;
  setOpen: (open: boolean, wasSuccessful?: boolean) => void; // Added wasSuccessful parameter for parent
  isLate: boolean;
  hasBeenDosed: boolean;
  displayAllActions: boolean;
  selectedSyringeDriverDrugs?: HSPackedMedication[];
  syringeDriverRun?: SyringeDriverRunDto;
  doseTime?: string;
  packedMedications?: HSPackedMedication[];
  secondCheckData?: SecondCheckData;
  onSelectedSyringeDriverDrugsChange?: (selected: HSPackedMedication[]) => void; // Add callback
}

export function SyringeDriverRunAdministerDialog(props: SyringeDriverRunAdministrationFormProps) {
  const apiUtils = useApiUtils();
  const services = useSyncCenter();
  const roundStore = useMap(services.rounds);
  const [syringeDrivers, setSyringeDrivers] = useState<HSPackedMedication[] | undefined>([]);

  // Use props.selectedSyringeDriverDrugs if provided, otherwise use local state
  const [localSelectedSyringeDriverDrugs, setLocalSelectedSyringeDrivers] = useState<HSPackedMedication[]>(
      props.packedMedication ? [props.packedMedication] : []
  );

  // Determine which state to use - parent's or local
  const selectedSyringeDriverDrugs = props.onSelectedSyringeDriverDrugsChange
      ? props.selectedSyringeDriverDrugs ?? []
      : localSelectedSyringeDriverDrugs;

  const updateSelectedSyringeDriverDrugs = (newSelection: HSPackedMedication[]) => {
      if (props.onSelectedSyringeDriverDrugsChange) {
          // If parent provided callback, update parent state
          props.onSelectedSyringeDriverDrugsChange(newSelection);
      } else {
          // Otherwise update local state
          setLocalSelectedSyringeDrivers(newSelection);
      }
  };

  const [requiresSecondCheck, setRequiresSecondCheck] = useState(false);

  useEffect(() => {
    for (const pm of selectedSyringeDriverDrugs) {
      const secondCheck = apiUtils.residentDetails.isMedicationSecondCheckable(props.facilityGroupId, props.administrationPermissions.canAdministerPatchMedicationExcludingS8 ?? false, pm);
        const secondCheckInfo = props.secondCheckData?.secondCheckableMedication[pm.hsId!];
      if (secondCheck && !secondCheckInfo?.userName) {
        setRequiresSecondCheck(true);
        return;
      }
    }
    setRequiresSecondCheck(false);
  }, [selectedSyringeDriverDrugs, props.secondCheckData]);

  const roundWindow = props.currentRound?.createdAt
    ? apiUtils.rounds.getRoundWindow(DateUtils.toDate(props.currentRound.createdAt), props.facilityGroupId)
    : undefined;

  const isUpdateMode = !!props.syringeDriverRun;

  const addToSelectedSyringeDriversArray = (event: any, hsPackedMedication: HSPackedMedication): void => {
      if (event.target.checked && !selectedSyringeDriverDrugs.some(x => x.hsId === hsPackedMedication.hsId)) {
          updateSelectedSyringeDriverDrugs([...selectedSyringeDriverDrugs, hsPackedMedication]);
      }
      if (!event.target.checked && selectedSyringeDriverDrugs.some(x => x.hsId === hsPackedMedication.hsId)) {
          updateSelectedSyringeDriverDrugs(
              selectedSyringeDriverDrugs.filter((syringeDriver) => syringeDriver.hsId !== hsPackedMedication.hsId)
          );
      }
  };

  const handleCloseDialog = (): void => {
    props.setOpen(false); // Note: Not passing wasSuccessful here (just close with X)
      // Don't clear selections - they're managed by parent now
  };

  useEffect(() => {
    const allSyringeDrivers: HSPackedMedication[] = [];
    props.packedMedications?.forEach((hsPackedMedication: HSPackedMedication) => {
      if (props.isLate || (roundWindow && hsPackedMedication.doseTimestamp && roundWindow.start <= hsPackedMedication.doseTimestamp &&
          roundWindow.end >= hsPackedMedication.doseTimestamp)) {
        // See if it is already used.
        const roundHierarchies = getRoundHierarchiesForPackedMedication(roundStore,  hsPackedMedication);
        if (roundHierarchies.length === 0 || (roundHierarchies[0].administeredDrug.reasonCode !== ReasonCode.Dosed && roundHierarchies[0].administeredDrug.reasonCode !== ReasonCode.DosedLate)) {
          // Not in use.
          allSyringeDrivers.push(hsPackedMedication);
        }
      }
    });
    setSyringeDrivers(allSyringeDrivers);
  }, [props.packedMedications, props.isLate, props.currentRound]);


  return (
    <Dialog lazy closeButtonColor={'#fff'} open={props.open} onRequestClose={handleCloseDialog} size="full">
      <DialogContainer horizontal gap={1} data-testid="medicine-administer-dialog">
        <SyringeDriversListSection>
          {syringeDrivers && syringeDrivers.length > 0 &&
              <TitleContainer>
                {
                  syringeDrivers && syringeDrivers.length > 0 && <WhiteText size="large">Available Syringe Drivers for {DateUtils.toDisplayString(syringeDrivers[0].doseTimestamp)}</WhiteText> 
                }
                  
                  <WhiteText size="small" style={{margin: '1.5em'}}>Select medication from the list below and
                      fill out the form.</WhiteText>
              </TitleContainer>
          }

          <SyringeDriversListContainer>
            {/* Renders syringe drivers list */}
            {syringeDrivers?.map((hsPackedMedication: HSPackedMedication, ndx) => {
                return (
                  <SyringeDriverListItemContainer key={ndx}>
                    <Checkbox key={ndx + 1} checked={selectedSyringeDriverDrugs.some(x => x.hsId === hsPackedMedication.hsId)} disabled={isUpdateMode}
                              onChange={(event) => addToSelectedSyringeDriversArray(event, hsPackedMedication)}></Checkbox>
                    <MedicationInformationLabel
                      packedMedication={hsPackedMedication}
                      facilityGroupId={props.facilityGroupId} patient={props.patient}
                      drug={props.drug}
                      administrationPermissions={props.administrationPermissions}
                      selectedDate={props.selectedDate}
                      secondCheckData={props.secondCheckData}/>
                  </SyringeDriverListItemContainer>
                )
              }
            )}
          </SyringeDriversListContainer>
        </SyringeDriversListSection>
        <AdministerDrugForm selectedSyringeDriverDrugs={selectedSyringeDriverDrugs} {...props} requiresSecondCheck={requiresSecondCheck}/>
      </DialogContainer>
    </Dialog>
  );
}

interface FormFields {
  status: ReasonCode;
  notes?: string;
  totalToBeInfused?: number;
  amountInfused?: number;
  remainingToBeInfused?: number;
  rateOfInfusion?: number;
  endDate?: Date;
  siteLocation?: string;
  siteLocationOther?: string;
  diluent?: string;
  syringeDriverNumber?: string;
  hsPackedMedications: HSPackedMedication[];
}

enum Diluent {
  WaterForInjection = 'Water for Injection',
  NormalSaline = 'Normal Saline (Sodium Chloride 0.9%)',
  Other = 'Other',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
interface AdministerDrugFormProps extends SyringeDriverRunAdministrationFormProps {
  requiresSecondCheck: boolean;
}
function AdministerDrugForm(props: AdministerDrugFormProps) {
  const apiUtils = useApiUtils();
  const groupPermissions = useGroupPermissions();
  const services = useSyncCenter();
  const drugStore = useMap(services.drugs);

  const roundSchedule = useRoundSchedule();
  const showAdministerLate = props.isLate && !props.hasBeenDosed;
  const startReasonCodes: ReasonCode[] = showAdministerLate ? [ReasonCode.DosedLate] : [ReasonCode.Dosed];
  const canRecordNote = groupPermissions.canRecordNote;
  const user = useCurrentUser();
  const reasonCodes: Array<ReasonCode> = [...startReasonCodes, ...stopReasonCodes];
  const hsUserMap = useMap(services.users);
  useEffect(() => {
    const interval = setInterval(() => {
      handleRateChange();
    }, 1000 * 60);

    return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, []);

  const hsUser = hsUserMap.get(user.profile.sub) ?? {
    subjectId: user.profile.sub,
    firstName: "",
    familyName: ""
  };
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues:
      {
          status: (props.requiresSecondCheck && !showAdministerLate ? ReasonCode.Refused : ReasonCode.Dosed),
          notes: props.syringeDriverRun?.comment ?? undefined,
          totalToBeInfused: props.syringeDriverRun?.infusion?.amountTotalMl ?? undefined,
          diluent: props.syringeDriverRun?.diluent ?? undefined,
          syringeDriverNumber: props.syringeDriverRun?.syringeDriverIdentifier ?? undefined, 
          rateOfInfusion: props.syringeDriverRun?.infusion?.rateMlPerH ?? undefined,
          siteLocation: props.syringeDriverRun?.site?.position ? syringeDriverSiteLocations.includes(props.syringeDriverRun.site?.position)
              ? props.syringeDriverRun.site?.position
              : 'Other'
            : undefined,
          siteLocationOther: props.syringeDriverRun?.site?.position ?? undefined,
          endDate: props.syringeDriverRun?.scheduledEnd ? DateUtils.toDate(props.syringeDriverRun?.scheduledEnd) : undefined 
        }
  });
  const currentStatus = form.watch('status');
  // when action button says Second Check, user can still bring up the dialog via 3 dots button hence props.requiresSecondCheck is being used to decide which components to render
  const disableForm = (props.selectedSyringeDriverDrugs?.length === 0) && !props.requiresSecondCheck;

  const statusOptions = reasonCodes.map((status) => {
    return {
      label: status === ReasonCode.Other ? 'Other unadministered' : apiUtils.residentDetails.reasonCodeToString(status),
      value: status,
    };
  });

  const diluentOptions = [
    Diluent.WaterForInjection,
    Diluent.NormalSaline,
    Diluent.Other
  ];
  const identifierOptions = [
    "1",
    "2",
    "3",
    "4",
    "5"
  ];


  async function onSubmit(data: FormFields) {
    if (data.notes && !canRecordNote) {
      toasts.error('You do not have permission to record notes');
      return;
    }

    try {
        // important: this is not one of form inputs but processDrugAdministration() needs to know about the selected syringe drivers
        data.hsPackedMedications = props.selectedSyringeDriverDrugs as HSPackedMedication[];

        const newRound = await processDrugAdministration(
          data,
          props,
          user,
          hsUser,
          apiUtils.residentDetails,
          services.syringeDriverRun.service,
          drugStore
        );

        // Refresh round schedule
        if (props.currentRound) {
          roundSchedule.set({
            ...(newRound ?? props.currentRound),
            version: ((newRound ?? props.currentRound).version ?? 0) + 1,
          });
        }

        // Close dialog and indicate success - this will trigger clearing of selections in parent
        props.setOpen(false, true); // true = wasSuccessful
    } catch (error) {
        // Handle error - don't clear selections
        toasts.error('Failed to submit');
        // Don't close dialog or clear selections
    }
  }

  function validateNotes(notes: string | undefined): string | undefined {
    const status = form.watch('status');
    if (!status) {
      return;
    }
    if (status === ReasonCode.DosedLate && (!notes || notes.length === 0)) {
      return 'Notes unless self administered';
    }
    if (form.watch('status') !== ReasonCode.Dosed && (!notes || notes.length === 0)) {
      return 'Notes required if not administered or self administered';
    }
  }


  function handleRateChange() {
    const rate = form.getValues().rateOfInfusion;
    const total = form.getValues().totalToBeInfused;
    if (rate && rate > 0 && total && total > 0) {
      const numSeconds = Math.min(25 * 3600, total / rate * 3600);
      const endAt = addSeconds(new Date(), numSeconds);
      form.setValue("endDate", endAt);
    }
  }

    const hasMultipleDrugsForSyringeDriver = (): boolean => {
        const total = props.packedMedications?.length ?? 0;
        return total > 1;
    };

    const [syringeDriverConfirmMultipleDrugsDialogIsOpen, setSyringeDriverConfirmMultipleDrugsDialogOpen] = React.useState(false);

    // Click handler that validates form, then either opens the dialog or submits
    const handlePrimaryActionClick = React.useCallback(async () => {

        if (disableForm) return;

        // Trigger form validation
        const isValid = await form.trigger();

        if (!isValid) {
            // If validation fails, don't proceed
            return;
        }

        // If validation passes, check if we need to show the dialog, which is the case if there are multiple drugs for the syringe driver.
        // Prompts the user that they made sure they have all the ones they require selected, whether it be just one, or all, or somewhere in between.
        if (hasMultipleDrugsForSyringeDriver()) {
            setSyringeDriverConfirmMultipleDrugsDialogOpen(true);
        } else {
            // Submit the form programmatically (same as Button type="submit")
            await form.handleSubmit(onSubmit)();
        }
    }, [disableForm, form, onSubmit, hasMultipleDrugsForSyringeDriver]);

    return (
    <Form form={form} onSubmit={onSubmit}>
      <Layout gap={1}>
        <TitleContainer>
          <WhiteText size="large">Current Syringe Driver Status</WhiteText>
          <Line></Line>
        </TitleContainer>
        {/* Adds syringes to the top right hand side of the page */}
        <Grid cols={2} gap={1}>
          {props.selectedSyringeDriverDrugs && props.selectedSyringeDriverDrugs?.length > 0 && props.selectedSyringeDriverDrugs?.map((syringeDriver) => (
            <MedicationInformationLabel key={syringeDriver.hsId} patient={props.patient}
                                        packedMedication={syringeDriver}
                                        facilityGroupId={props.facilityGroupId}
                                        administrationPermissions={props.administrationPermissions}
                                        selectedDate={props.selectedDate} secondCheckData={props.secondCheckData}/>
          ))}
        </Grid>
        {/* Shows when syringe driver hasn't been administered yet */}
        {!props.status && props.selectedSyringeDriverDrugs?.length === 0 && !props.requiresSecondCheck &&
            <CurrentStatusPlaceholder>
                <Text size="small">Select medication from the list on the left hand side</Text>
            </CurrentStatusPlaceholder>
        }
        <UpdateStatusSection gap={1}>
          <TitleContainer>
            <Text size="large">Update Syringe Driver Status</Text>
            <Line></Line>
          </TitleContainer>
          <RadioGroup
            name="status"
            options={statusOptions}
            cols={3}
            control={form.control}
            required
            disabled={disableForm}
            onChange={async () => await form.trigger(["notes"])}
          />
        </UpdateStatusSection>
        {startReasonCodes.includes(form.watch('status')) && (
          <Layout gap={1}>
            <Grid cols={3}>
              <FormGroup label="Total volume to be infused (ml)" isRequired fullWidth>
                <TextInput
                  type="number"
                  name="totalToBeInfused"
                  fullWidth
                  disabled={disableForm}
                  onChange={handleRateChange}
                  step="0.01"
                  rules={{
                    validate: (total:number) => {
                      if (total <= 0) {
                        return 'Total infused must be > 0';
                      }
                      return true;
                    },
                  }}
                />
              </FormGroup>
              <FormGroup label="Rate of infusion (ml/hr)" isRequired fullWidth>
                <TextInput
                  type="number"
                  name="rateOfInfusion"
                  fullWidth
                  required
                  disabled={disableForm}
                  onChange={handleRateChange}
                  step="0.01"
                  rules={{
                    validate: (rate:number) => {
                      if (rate <= 0) {
                        return 'Rate of infusion must be > 0';
                      }
                      return true;
                    },
                  }}
                />
              </FormGroup>
              <FormGroup label="End date and time" isRequired fullWidth>
                <DateInput
                  name="endDate"
                  dateTime
                  fullWidth
                  required
                  rules={{
                    validate: (endDate: Date) => {
                      if (endDate < new Date()) {
                        return 'Date must be in the future';
                      }
                      if (!isToday(endDate) && !isToday(subDays(endDate, 1))) {
                        return 'Date can only end up to one day from administration';
                      }

                      return true;
                    },
                  }}
                  disabled={true}
                />
              </FormGroup>
              <FormGroup label="Site Location" isRequired fullWidth>
                <Select
                  name="siteLocation"
                  options={syringeDriverSiteLocations.map((option) => ({label: option, value: option}))}
                  fullWidth
                  required
                  disabled={disableForm}
                />
                {form.watch('siteLocation') === 'Other' && (
                  <TextInput
                    name="siteLocationOther"
                    placeholder="Other site location"
                    fullWidth
                    required
                    disabled={disableForm}
                  />
                )}
              </FormGroup>
              <FormGroup label="Diluent" isRequired fullWidth>
                <Select name="diluent"
                        options={diluentOptions.map((option: Diluent) => ({label: option, value: option}))}
                        disabled={disableForm}
                        required fullWidth></Select>
              </FormGroup>
              <FormGroup label="Syringe Driver Identifier" isRequired fullWidth>
                <Select
                    name="syringeDriverNumber"
                    options={identifierOptions.map((option) => ({ label: option, value: option }))}
                    disabled={disableForm}
                    required
                    fullWidth
                />
              </FormGroup>
            </Grid>
          </Layout>
        )}
        <FormGroup label="Notes" fullWidth>
          <TextAreaContainer>
            <TextArea
              name="notes"
              placeholder="Add note"
              rows={3}
              fullWidth
              rules={{
                validate: (notes: string) => validateNotes(notes)
              }}
              data-testid="medication-notes"
              disabled={disableForm}
            />
          </TextAreaContainer>
        </FormGroup>
          {/* Don't show the second check dialog, when it is a drug that needs it, if the current status is such that the drug is not actually getting administered. */}
          {props.requiresSecondCheck
              && (!currentStatus || currentStatus === ReasonCode.Dosed || currentStatus === ReasonCode.DosedLate) ? (
              <SyringeDriverSecondCheckDialogContainer {...props} />
          ) : (
              <>
                  <Button
                      style={{ margin: "3em 0" }}
                      data-testid="medication-administer-button"
                      disabled={disableForm}
                      onClick={handlePrimaryActionClick}
                  >
                      {(!currentStatus || currentStatus === ReasonCode.Dosed || currentStatus === ReasonCode.DosedLate)
                          ? "Start Syringe Driver Run"
                          : "Update Syringe Driver Run"}
                  </Button>

                  <SyringeDriverConfirmMultipleDrugsDialog
                      isOpen={syringeDriverConfirmMultipleDrugsDialogIsOpen}
                      setClosed={() => setSyringeDriverConfirmMultipleDrugsDialogOpen(false)}
                      onConfirm={() => form.handleSubmit(onSubmit)()}
                  />
              </>
          )}

      </Layout>
    </Form>
  );
}

const DialogContainer = styled(Layout)`
    padding: 1em;
    grid-template-columns: 3fr 5fr;
`;

const SyringeDriversListSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  width: "100%";
`;

const SyringeDriversListContainer = styled.div`
    //height: 600px;
    overflow-y: auto;
    scrollbar-width: none;
`;

const SyringeDriverListItemContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    color: white;
    margin: 1em 3em;
    z-index: 1000;
    @media screen and (min-width: 600px) {
        margin: 1em;
    }
`;

const CurrentStatusPlaceholder = styled.div`
    display: flex;
    justify-content: center;
    background-color: rgba(245, 245, 245, .6);
    border: 1px solid rgba(255, 255, 255, .6);
    border-radius: 2px;
    padding: 1em 2em;
    color: black;
    width: 50%;
    margin: 1em auto;
`;

const UpdateStatusSection = styled(Layout)`
    color: white;
    margin: 2em 0;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const WhiteText = styled(Text)`
    color: white;
`;

const Line = styled.hr`
    border: 1px solid ${colors.bg_green_400};
    margin: .5em 0;
`;

const TextAreaContainer = styled.div`
    margin-top: .25em;
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
async function processDrugAdministration(
  data: FormFields,
  props: SyringeDriverRunAdministrationFormProps,
  user: User,
  hsuser: HSUser,
  residentDetails: ResidentDetailsUtils,
  syringeDriverRunService: SyncSyringeDriverRuns, 
    drugStore: ReadonlyMap<string,  HSDrug>
) {
  const notes = data.notes ? [data.notes] : [];
  
  // Relates the clinical system id of the administered drug to the packed med it was created from.
  const adrugMap: {[clinicalSystemId: string ]:HSPackedMedication} = {}
  
  const adrugs: (HSAdministeredDrug | HSAdministeredAdHocDrug)[] = [];
  for (const packedMed of props.selectedSyringeDriverDrugs!) {
      const drug = drugStore.get(packedMed.drugHsId!.toString());
      const secondCheckInfo = props.secondCheckData?.secondCheckableMedication[packedMed.hsId!];
      adrugs.push(residentDetails.generateAdministeredDrug(
          packedMed,
          drug!,
          data.status!,
          notes,
          user,
          secondCheckInfo?.userName,
          secondCheckInfo?.hsId,
          props.packedMedication?.dosage,
      ));
  }
  const queuedDrugs = await props.onAdminister?.(adrugs,
        props.packedMedication && 'doseTimestamp' in props.packedMedication
            ? props.packedMedication?.doseTimestamp ?? ''
            : '',
    );
  if (queuedDrugs && props.selectedSyringeDriverDrugs) {
      queuedDrugs.administeredDrugs.forEach((queuedDrug,  idx) => {
          adrugMap[queuedDrug.clinicalSystemId!] = props.selectedSyringeDriverDrugs![idx];
      });
  }
  if (data.status == ReasonCode.Dosed || data.status == ReasonCode.DosedLate) {
    // Only start a syringe driver run if we actually start the syringe driver.
    // TODO:  Add second check.
    if (!queuedDrugs || queuedDrugs.administeredDrugs.length == 0) {
      return undefined;
    }
    
    await syringeDriverRunService.enqueue.createSyringeDriverRun({
          syringeDriverIdentifier: data.syringeDriverNumber, 
          diluent: data.diluent, 
          site: {
            position: data.siteLocation === 'Other' ? data.siteLocationOther : data.siteLocation
          },
          infusion: {
            rateMlPerH: data.rateOfInfusion,
            amountRemainingMl: data.totalToBeInfused,
            amountTotalMl: data.totalToBeInfused, 
            amountAdministeredMl: 0, 
          },
          clientCreatedAt: DateUtils.fromDate(new Date()),
          clientUser: {
            firstName: hsuser.firstName, 
            familyName: hsuser.familyName, 
            designation: hsuser.designation, 
            subjectId: hsuser.subjectId
          }, 
          scheduledEnd: DateUtils.toOffsetlessString(data.endDate),
          activityType: SyringeDriverRunActivityTypeDto.Start,
          comment: data.notes,
        }, hsuser.subjectId!, props.patient.facility!, props.patient.hsId ?? -1,
        queuedDrugs.administeredDrugs.map(x => {
          return {
            administeredDrugClinicalSystemId: x.clinicalSystemId, 
            medicationId: props.packedMedication?.medicationId, 
            medicationVersion: props.packedMedication?.medicationVersion
          }
        }),
        queuedDrugs.round?.clinicalSystemId ?? undefined);
  }
  return queuedDrugs?.round;
}

const stopReasonCodes: ReasonCode[] = [
  ReasonCode.Refused,
  ReasonCode.NoStock,
  ReasonCode.Withheld,
  ReasonCode.Hospital,
  ReasonCode.Absent,
  ReasonCode.Ceased,
  ReasonCode.Omitted,
  ReasonCode.Leave,
  ReasonCode.Other,
];

export const syringeDriverSiteLocations = [
  'Abdomen Left',
  'Abdomen Right',
  'Upper anterior thighs, Left',
  'Upper anterior thighs, Right',
  'Anterior chest, Left',
  'Anterior chest, Right',
  'Scapula, Left',
  'Scapula, Right',
  'Anterior aspect of upper arms, Left',
  'Anterior aspect of upper arms, Right',
  'Other',
];
function SyringeDriverSecondCheckDialogContainer(props: SyringeDriverRunAdministrationFormProps) {

  // Do 1 med at a time.
  const targetMed = props.selectedSyringeDriverDrugs?.find(x => !!props.secondCheckData?.secondCheckableMedication[x.hsId!] && !props.secondCheckData?.secondCheckableMedication[x.hsId!]?.userName);
  if (!targetMed) {
    return null;
  }
  const updatedProps = {...props, packedMedication: targetMed, packedMedications: undefined }
  return (
      <SecondCheckDialog
          { ...updatedProps } 
          onSubmit={(secondCheckUser) => {
            if (!props.selectedSyringeDriverDrugs) {
              return;
            }
            props.secondCheckData?.setMedicationSecondChecked({
              id: targetMed.hsId!,
              userName: secondCheckUser.email!,
              hsId: secondCheckUser.hsId,
              date: new Date(),
              medication: targetMed
            });
          }}
      />
  );
}
